import { useEffect } from "react"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { useAddColumnsMutation } from "./use-add-columns-mutation"
import { useAIParserSession } from "./use-ai-parser-session"
import { type AddColumnsFormData, createAddColumnsSchema } from "../schemas/column-schema"

type UseAddColumnsProps = {
  onSuccess: () => void
  isOpen: boolean
}

export function useAddColumns({ onSuccess, isOpen }: UseAddColumnsProps) {
  const addColumnsMutation = useAddColumnsMutation()

  const { setCurrentSessionId } = useAIParserSession()

  const { t } = useTranslation()

  const addColumnsSchema = createAddColumnsSchema(t)

  const form = useForm<AddColumnsFormData>({
    resolver: zodResolver(addColumnsSchema),
    defaultValues: {
      columns: [{ name: "", dataType: "string", description: "", options: [] }],
    },
  })

  useEffect(() => {
    form.reset()
  }, [isOpen])

  const addColumn = () => {
    const currentColumns = form.getValues("columns")
    form.setValue("columns", [
      ...currentColumns,
      { name: "", dataType: "string", description: "", options: [] },
    ])
  }

  const deleteColumnFromForm = (columnIdToDelete: number) => {
    const currentColumns = form.getValues("columns")
    if (currentColumns.length > 1) {
      form.setValue(
        "columns",
        currentColumns.filter((_, currentColumnId) => currentColumnId !== columnIdToDelete)
      )
    }
  }

  const addColumns = async (data: AddColumnsFormData) => {
    await addColumnsMutation.mutateAsync(
      {
        columns: data.columns,
      },
      {
        onSuccess: (data) => {
          form.reset()
          setCurrentSessionId(data.session_id)
          onSuccess()
        },
      }
    )
  }

  return {
    form,
    addColumn,
    deleteColumnFromForm,
    addColumns,
    addColumnsMutation,
  }
}
